import { Action } from '@ngrx/store';
import { zefGo } from '@zerops/zef/ngrx-router';
import { zefDialogOpen } from '@zerops/zef/dialog';
import { authActions } from '@zerops/zef/auth';
import { ThemeService } from '@zerops/zef/theme';
import { ServiceStackTypeCategories } from '@zerops/models/service-stack';
import {
  ServiceStackOperationTypes,
  serviceStackReload,
  serviceStackRestart,
  serviceStackStart,
  serviceStackStop
} from '@zerops/zerops/core/service-stack-base';
import {
  ImportExportDialogModes,
  importExportDialogOpen
} from '@zerops/zerops/feature/import-export-dialog';
import { CLIENT_ADD_DIALOG_FEATURE_NAME } from '@zerops/zerops/feature/client-add-dialog';
import { openTopUpDialog } from '@zerops/zerops/feature/top-up-dialog';
import { PaletteItem, PalettePermissions } from './command-palette.model';

export interface CommandBuilderDeps {
  dispatch: (action: Action) => void;
  theme: ThemeService;
  permissions: PalettePermissions;
}

export const buildStaticCommands = ({
  dispatch,
  theme,
  permissions
}: CommandBuilderDeps): PaletteItem[] => {
  const items: PaletteItem[] = [];

  // -- Navigation
  items.push({
    id: 'command:nav-projects',
    type: 'command',
    label: 'Projects',
    icon: 'dashboard',
    keywords: 'dashboard home',
    group: 'Navigation',
    run: () => dispatch(zefGo([ '/dashboard', 'projects' ]))
  });

  items.push({
    id: 'command:nav-team',
    type: 'command',
    label: 'Team',
    icon: 'people_outline',
    keywords: 'users members',
    group: 'Navigation',
    run: () => dispatch(zefGo([ '/dashboard', 'users' ]))
  });

  if (permissions.canCreateProjects) {
    items.push({
      id: 'command:nav-project-add',
      type: 'command',
      label: 'Add project',
      icon: 'add',
      keywords: 'new create project',
      group: 'Navigation',
      run: () => dispatch(zefGo([ '/dashboard', 'project-add' ]))
    });
  }

  if (permissions.hasAdminRoleOrHigher) {
    items.push({
      id: 'command:nav-user-add',
      type: 'command',
      label: 'Add team member',
      icon: 'person_add',
      keywords: 'invite user new',
      group: 'Navigation',
      run: () => dispatch(zefGo([ '/dashboard', 'user-add' ]))
    });
  }

  if (permissions.canViewFinances) {
    items.push({
      id: 'command:nav-finances',
      type: 'command',
      label: 'Credit & Spend Overview',
      icon: 'monetization_on',
      keywords: 'finances costs billing money',
      group: 'Navigation',
      run: () => dispatch(zefGo([ '/dashboard', 'finances' ]))
    });
  }

  if (permissions.hasOwnerRole) {
    items.push({
      id: 'command:nav-company',
      type: 'command',
      label: 'Organization Settings',
      icon: 'business',
      keywords: 'company organization',
      group: 'Navigation',
      run: () => dispatch(zefGo([ '/settings', 'company' ]))
    });
  }

  if (permissions.canViewFinances) {
    items.push({
      id: 'command:nav-billing',
      type: 'command',
      label: 'Invoices & Billing Settings',
      icon: 'receipt_long',
      keywords: 'invoices payments billing',
      group: 'Navigation',
      run: () => dispatch(zefGo([ '/settings', 'billing' ]))
    });
  }

  items.push({
    id: 'command:nav-account',
    type: 'command',
    label: 'Account Settings',
    icon: 'person',
    keywords: 'profile settings',
    group: 'Navigation',
    run: () => dispatch(zefGo([ '/settings', 'account' ]))
  });

  items.push({
    id: 'command:nav-security',
    type: 'command',
    label: 'Security & Authentication',
    icon: 'shield',
    keywords: 'password 2fa two factor',
    group: 'Navigation',
    run: () => dispatch(zefGo([ '/settings', 'security' ]))
  });

  items.push({
    id: 'command:nav-tokens',
    type: 'command',
    label: 'Access Tokens Management',
    icon: 'vpn_key',
    keywords: 'api token key',
    group: 'Navigation',
    run: () => dispatch(zefGo([ '/settings', 'token-management' ]))
  });

  items.push({
    id: 'command:nav-recipes',
    type: 'command',
    label: 'Recipes',
    icon: 'auto_awesome',
    keywords: 'templates examples',
    group: 'Navigation',
    run: () => dispatch(zefGo([ '/recipes' ]))
  });

  // -- Quick actions
  items.push({
    id: 'command:action-import-project',
    type: 'command',
    label: 'Import project (YAML)',
    icon: 'file_upload',
    keywords: 'yaml import export',
    group: 'Quick actions',
    run: () => dispatch(importExportDialogOpen({ mode: ImportExportDialogModes.Import }))
  });

  items.push({
    id: 'command:action-create-organization',
    type: 'command',
    label: 'Create organization',
    icon: 'domain_add',
    keywords: 'new client company',
    group: 'Quick actions',
    // once-ever action — searchable, not in the default state
    defaultHidden: true,
    run: () => dispatch(zefDialogOpen({ key: CLIENT_ADD_DIALOG_FEATURE_NAME }))
  });

  if (permissions.canViewFinances) {
    items.push({
      id: 'command:action-top-up',
      type: 'command',
      label: 'Top up credit',
      icon: 'account_balance_wallet',
      keywords: 'money payment credit',
      group: 'Quick actions',
      defaultHidden: true,
      run: () => dispatch(openTopUpDialog())
    });
  }

  // -- Interface
  items.push({
    id: 'command:theme-light',
    type: 'command',
    label: 'Theme: Light',
    icon: 'light_mode',
    keywords: 'appearance color scheme',
    group: 'Interface',
    defaultHidden: true,
    run: () => theme.saveTheme('light')
  });

  items.push({
    id: 'command:theme-dark',
    type: 'command',
    label: 'Theme: Dark',
    icon: 'dark_mode',
    keywords: 'appearance color scheme',
    group: 'Interface',
    defaultHidden: true,
    run: () => theme.saveTheme('dark')
  });

  items.push({
    id: 'command:theme-system',
    type: 'command',
    label: 'Theme: System',
    icon: 'settings_brightness',
    keywords: 'appearance color scheme auto',
    group: 'Interface',
    defaultHidden: true,
    run: () => theme.saveTheme('system')
  });

  items.push({
    id: 'command:toggle-zen',
    type: 'command',
    label: 'Toggle inline help',
    icon: 'help_outline',
    keywords: 'zen mode tips hints',
    group: 'Interface',
    defaultHidden: true,
    run: () => theme.toggleZenMode()
  });

  items.push({
    id: 'command:toggle-compact',
    type: 'command',
    label: 'Toggle compact mode',
    icon: 'view_compact',
    keywords: 'layout density full',
    group: 'Interface',
    defaultHidden: true,
    run: () => theme.toggleCompactMode()
  });

  // -- Resources
  items.push({
    id: 'command:resource-docs',
    type: 'command',
    label: 'Documentation',
    icon: 'text_snippet',
    keywords: 'docs help guide',
    group: 'Resources',
    run: () => window.open('https://docs.zerops.io', '_blank')
  });

  items.push({
    id: 'command:resource-discord',
    type: 'command',
    label: 'Discord - community help',
    icon: 'question_answer',
    keywords: 'chat community support',
    group: 'Resources',
    run: () => window.open('https://discord.gg/zeropsio', '_blank')
  });

  items.push({
    id: 'command:resource-support-email',
    type: 'command',
    label: 'Support email',
    icon: 'alternate_email',
    keywords: 'help contact support',
    group: 'Resources',
    run: () => window.open('mailto:support@zerops.io', '_blank')
  });

  // -- Session
  items.push({
    id: 'command:session-logout',
    type: 'command',
    label: 'Logout',
    icon: 'logout',
    keywords: 'sign out exit',
    group: 'Session',
    defaultHidden: true,
    run: () => dispatch(authActions.logout())
  });

  return items;
};

const PROJECT_TABS: Array<{ tab: string; label: string; icon: string; keywords?: string; }> = [
  { tab: 'service-stacks', label: 'Services', icon: 'grid_view', keywords: 'stacks overview' },
  { tab: 'routing', label: 'Access & Routing', icon: 'public', keywords: 'domains subdomains ports' },
  { tab: 'envs', label: 'Environment variables', icon: 'code', keywords: 'env vars' },
  { tab: 'logging', label: 'Logs', icon: 'subject', keywords: 'logging' },
  { tab: 'backups', label: 'Backups', icon: 'settings_backup_restore', keywords: 'backup backups list' },
  { tab: 'observability', label: 'Observability', icon: 'insights', keywords: 'metrics monitoring' },
  { tab: 'finances', label: 'Cost', icon: 'monetization_on', keywords: 'finances spend' },
  { tab: 'settings', label: 'Settings', icon: 'settings' },
  { tab: 'add-service-stack', label: 'Add service', icon: 'add', keywords: 'new service stack' }
];

export const buildProjectContextCommands = (
  projectId: string,
  dispatch: (action: Action) => void,
  group = 'Current project'
): PaletteItem[] => PROJECT_TABS.map(({ tab, label, icon, keywords }) => ({
  id: `command:project:${projectId}:${tab}`,
  type: 'command',
  label,
  icon,
  keywords,
  group,
  run: () => dispatch(zefGo([ '/project', projectId, tab ]))
}));

const SERVICE_TABS: Array<{ tab: string; label: string; icon: string; keywords?: string; }> = [
  { tab: 'dashboard', label: 'Overview', icon: 'space_dashboard' },
  { tab: 'deploy', label: 'Pipelines & CI/CD', icon: 'rocket_launch', keywords: 'deploy build' },
  { tab: 'autoscaling', label: 'Autoscaling', icon: 'tune', keywords: 'resources scaling' },
  { tab: 'routing', label: 'Routing', icon: 'public', keywords: 'domains subdomains ports' },
  { tab: 'access-details', label: 'Access details', icon: 'vpn_key', keywords: 'credentials connection' },
  { tab: 'backup', label: 'Backups', icon: 'settings_backup_restore' },
  { tab: 'configuration', label: 'Configuration', icon: 'description', keywords: 'config' },
  { tab: 'user-data', label: 'Environment variables', icon: 'code', keywords: 'env vars user data' },
  { tab: 'file-browser', label: 'File browser', icon: 'folder_open', keywords: 'files' },
  { tab: 'log', label: 'Runtime log', icon: 'subject', keywords: 'logs' },
  { tab: 'terminal', label: 'Remote terminal', icon: 'terminal', keywords: 'shell ssh console' },
  { tab: 'finances', label: 'Cost', icon: 'monetization_on', keywords: 'finances spend' }
];

export const buildServiceContextCommands = (
  serviceId: string,
  dispatch: (action: Action) => void,
  group = 'Current service'
): PaletteItem[] => SERVICE_TABS.map(({ tab, label, icon, keywords }) => ({
  id: `command:service:${serviceId}:${tab}`,
  type: 'command',
  label,
  icon,
  keywords,
  group,
  run: () => dispatch(zefGo([ '/service-stack', serviceId, tab ]))
}));

// mirrors the three-dot service menu gating (service-stack-actions):
// no write access — no actions at all; object storage can't be
// started / stopped / restarted; reload is opt-in per stack. Deliberately NOT gated on
// service status — status flaps during deploys and would churn the
// index (the menu also shows Start and Stop simultaneously).
export const buildServiceActionCommands = (
  serviceId: string,
  dispatch: (action: Action) => void,
  group: string,
  meta: { fullAccess: boolean; typeCategory?: string; reloadAvailable?: boolean }
): PaletteItem[] => {
  if (!meta.fullAccess) { return []; }

  const items: PaletteItem[] = [];
  const isObjectStorage = meta.typeCategory === ServiceStackTypeCategories.ObjectStorage;

  if (!isObjectStorage) {
    items.push({
      id: `command:service:${serviceId}:action-start`,
      type: 'command',
      label: 'Start service',
      icon: 'play_arrow',
      keywords: 'start run',
      group,
      run: () => dispatch(serviceStackStart({ id: serviceId, type: ServiceStackOperationTypes.Start }))
    });

    items.push({
      id: `command:service:${serviceId}:action-stop`,
      type: 'command',
      label: 'Stop service',
      icon: 'stop',
      keywords: 'stop shutdown',
      group,
      run: () => dispatch(serviceStackStop({ id: serviceId, type: ServiceStackOperationTypes.Stop }))
    });

    items.push({
      id: `command:service:${serviceId}:action-restart`,
      type: 'command',
      label: 'Restart service',
      icon: 'restart_alt',
      keywords: 'restart reboot',
      group,
      run: () => dispatch(serviceStackRestart({ id: serviceId, type: ServiceStackOperationTypes.Restart }))
    });
  }

  if (meta.reloadAvailable) {
    items.push({
      id: `command:service:${serviceId}:action-reload`,
      type: 'command',
      label: 'Reload service',
      icon: 'refresh',
      keywords: 'reload',
      group,
      run: () => dispatch(serviceStackReload({ id: serviceId, type: ServiceStackOperationTypes.Reload }))
    });
  }

  return items;
};
